import React, { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Upload, Download, Loader2, Clock, CheckCircle2, HeartHandshake, MessageSquare, PenLine, Video, ExternalLink } from "lucide-react";
import { DocActionNudge } from "@/components/DocActionNudge";
import { DocDigestSettings } from "@/components/DocDigestSettings";

const STATUS = {
  requested: { label: "Requested by your guide", color: "#B7791F", icon: Clock },
  waiting_for_review: { label: "Sent — waiting for review", color: "#2C6480", icon: Clock },
  under_review: { label: "Being reviewed", color: "#2C6480", icon: Clock },
  needs_clarification: { label: "Needs your attention", color: "#9C1E22", icon: MessageSquare },
  needs_changes: { label: "Needs your attention", color: "#9C1E22", icon: MessageSquare },
  shared: { label: "Shared with you", color: "#1C4358", icon: FileText },
  approved: { label: "Approved", color: "#2F7D5A", icon: CheckCircle2 },
  uploaded: { label: "Uploaded", color: "#2F7D5A", icon: CheckCircle2 },
  ready_for_signing: { label: "Ready for signing", color: "#7C3AED", icon: PenLine },
  signing_pending: { label: "Signing in progress", color: "#7C3AED", icon: PenLine },
  signed: { label: "Signed", color: "#2F7D5A", icon: CheckCircle2 },
};
const sm = (s) => STATUS[s] || STATUS.shared;

const sectionOf = (d) => {
  if (["signed"].includes(d.status) || ["signed_externally", "completed"].includes(d.signing?.signing_status)) return "completed";
  if (d.signing) return "signing";
  if (d.status === "requested") return "requested";
  if (["needs_clarification", "needs_changes"].includes(d.status)) return "attention";
  if (d.meeting_id) return "meeting";
  if (d.uploaded_by_kind === "client") return "mine";
  return "shared";
};
const SECTIONS = [
  { key: "attention", title: "Needs your attention", hint: "Your guide asked a question or needs a change." },
  { key: "requested", title: "Requested documents", hint: "Please upload these when you can." },
  { key: "signing", title: "Signing documents", hint: "Documents being coordinated for signing." },
  { key: "meeting", title: "Meeting documents", hint: "Shared from your meetings." },
  { key: "shared", title: "Shared with you", hint: "Documents your guide has shared." },
  { key: "mine", title: "Uploaded by you / family", hint: "Documents you've provided." },
  { key: "completed", title: "Completed & signed", hint: "All done — kept for your records." },
];

export default function ClientDocuments() {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [respondDoc, setRespondDoc] = useState(null);
  const [respondText, setRespondText] = useState("");
  const fileRef = useRef(null);
  const rowFileRef = useRef(null);
  const rowDocId = useRef(null);

  const load = () => api.get("/portal/client/documents").then(({ data }) => setDocs(data.items || data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Please choose a file under 25MB."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("name", file.name);
    try { await api.post("/portal/client/documents/upload", fd); toast.success("Uploaded — thank you!"); load(); }
    catch (err) { toast.error(errMsg(err, "Upload failed. Please try again.")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const fulfil = (docId) => { rowDocId.current = docId; rowFileRef.current?.click(); };
  const onRowUpload = async (e) => {
    const file = e.target.files?.[0]; const docId = rowDocId.current;
    if (!file || !docId) return;
    setBusyId(docId);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/portal/client/documents/${docId}/upload`, fd); toast.success("Uploaded — thank you!"); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusyId(null); if (rowFileRef.current) rowFileRef.current.value = ""; }
  };

  const sendRespond = async () => {
    if (!respondText.trim()) return;
    setBusyId(respondDoc.id);
    try { await api.post(`/portal/client/documents/${respondDoc.id}/respond`, { message: respondText.trim() }); toast.success("Message sent to your guide."); setRespondDoc(null); setRespondText(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/portal/client/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.name || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "Could not download this document.")); }
  };

  if (docs === null) return <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>;

  const grouped = {};
  (docs || []).forEach((d) => { const s = sectionOf(d); (grouped[s] = grouped[s] || []).push(d); });
  const scrollToAction = () => { const el = document.querySelector('[data-testid^="client-doc-section-"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div data-testid="client-documents-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><FileText size={22} /> Your documents</h2>
          <p className="text-[15px] text-muted-foreground">Upload what your guide requested and keep everything in one safe place.</p>
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="client-doc-file-input" />
        <input ref={rowFileRef} type="file" className="hidden" onChange={onRowUpload} data-testid="client-doc-row-file-input" />
        <div className="flex items-center gap-2">
          <DocDigestSettings testid="client-doc-digest-settings" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-11 rounded-full px-5" data-testid="client-doc-upload-btn">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload a document
          </Button>
        </div>
      </div>

      <DocActionNudge endpoint="/portal/documents/action-summary" testid="client-doc-action-nudge" onOpen={scrollToAction} />

      {(!docs || docs.length === 0) ? (
        <Card className="p-10 text-center border-[#E6EDF2]" data-testid="client-documents-empty">
          <HeartHandshake size={40} className="mx-auto text-[#1C4358]/40 mb-3" />
          <div className="font-display font-semibold text-[18px] text-[#252221]">No documents yet</div>
          <p className="text-[15px] text-muted-foreground mt-1">When your guide requests a document, it will appear here. You can also upload anything helpful now.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {SECTIONS.filter((sec) => (grouped[sec.key] || []).length).map((sec) => (
            <div key={sec.key} data-testid={`client-doc-section-${sec.key}`}>
              <div className="mb-2">
                <h3 className="font-display font-semibold text-[16px] text-[#1C4358]">{sec.title}</h3>
                <p className="text-[13px] text-muted-foreground">{sec.hint}</p>
              </div>
              <div className="space-y-2.5">
                {grouped[sec.key].map((d) => {
                  const s = sm(d.status); const Icon = s.icon;
                  const canUpload = ["requested", "needs_clarification", "needs_changes"].includes(d.status);
                  const canDownload = d.uploaded && ["shared", "approved", "signed", "uploaded"].includes(d.status);
                  return (
                    <Card key={d.id} className="p-4 border-[#E6EDF2]" data-testid={`client-doc-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="h-10 w-10 rounded-xl bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><FileText size={19} /></span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[15px] font-medium text-[#252221] truncate">{d.name}</div>
                          <div className="text-[13px] flex items-center gap-1 font-medium" style={{ color: s.color }}><Icon size={13} /> {s.label}{d.meeting_title ? ` · ${d.meeting_title}` : ""}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {canUpload && (
                            <Button size="sm" onClick={() => fulfil(d.id)} disabled={busyId === d.id} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-9 rounded-full" data-testid={`client-doc-upload-row-${d.id}`}>
                              {busyId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                            </Button>
                          )}
                          {["needs_clarification", "needs_changes"].includes(d.status) && (
                            <Button size="sm" variant="outline" onClick={() => setRespondDoc(d)} className="border-[#D6E0E7] text-[#1C4358] gap-1.5 h-9 rounded-full" data-testid={`client-doc-respond-${d.id}`}><MessageSquare size={14} /> Reply</Button>
                          )}
                          {canDownload && (
                            <Button size="sm" variant="outline" onClick={() => download(d)} className="border-[#D6E0E7] text-[#1C4358] gap-1.5 h-9 rounded-full" data-testid={`client-doc-download-${d.id}`}><Download size={14} /> Download</Button>
                          )}
                        </div>
                      </div>
                      {d.external_response && (
                        <div className="mt-2.5 ml-13 rounded-lg bg-[#F4F8FB] border border-[#E6EDF2] px-3 py-2 text-[13px] text-[#3A4A52]" data-testid={`client-doc-note-${d.id}`}>
                          <span className="font-medium text-[#1C4358]">Your guide: </span>{d.external_response}
                        </div>
                      )}
                      {d.signing && (
                        <div className="mt-2.5 rounded-lg bg-[#F7F3FF] border border-[#E9DEFB] px-3 py-2 text-[13px]" data-testid={`client-doc-signing-${d.id}`}>
                          <div className="flex items-center gap-1.5 text-[#7C3AED] font-medium"><PenLine size={13} /> Signing: {d.signing.signing_status_label}</div>
                          {d.signing.external_signing_note && <div className="text-[#3A4A52] mt-1">{d.signing.external_signing_note}</div>}
                          {d.signing.external_signing_link && <a href={d.signing.external_signing_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#7C3AED] font-medium mt-1 hover:underline" data-testid={`client-doc-signing-link-${d.id}`}><ExternalLink size={12} /> Open signing link</a>}
                          {d.signing.meeting_id && <div className="inline-flex items-center gap-1 text-[#1C4358] mt-1"><Video size={12} /> A meeting has been scheduled.</div>}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!respondDoc} onOpenChange={(o) => { if (!o) { setRespondDoc(null); setRespondText(""); } }}>
        <DialogContent data-testid="client-doc-respond-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1C4358]">Reply to your guide</DialogTitle>
            <DialogDescription className="text-[13px]">{respondDoc?.external_response ? `“${respondDoc.external_response}”` : "Send a note about this document."}</DialogDescription>
          </DialogHeader>
          <Textarea value={respondText} onChange={(e) => setRespondText(e.target.value)} rows={3} placeholder="Type your reply…" data-testid="client-doc-respond-text" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRespondDoc(null); setRespondText(""); }}>Cancel</Button>
            <Button onClick={sendRespond} disabled={busyId === respondDoc?.id} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white" data-testid="client-doc-respond-send">Send reply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
